import { useMemo, useState, type ReactNode } from 'react';
import { Filter, SlidersHorizontal, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  Input,
  Skeleton,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  FilterBuilder,
  type ColumnDef,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { AccountBreadcrumbs } from './account-breadcrumbs';

/** A column with a stable id + a label used by the Display-options toggle. */
export type ScaffoldColumn<T> = ColumnDef<T, unknown> & { id: string; label?: string };

export interface AccountListScaffoldProps<T> {
  title: ReactNode;
  /** Breadcrumb current-page label — renders `Account › <label>` above the title. */
  breadcrumbLabel?: ReactNode;
  description?: ReactNode;
  primaryAction?: ReactNode;
  /** Optional content between the header and the toolbar (e.g. stat cards). */
  banner?: ReactNode;
  searchPlaceholder?: string;
  rows: T[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  getRowId: (row: T) => string;
  columns: ScaffoldColumn<T>[];
  /** Column ids shown in Display options (others are always visible). */
  toggleableIds?: string[];
  /** Text used for client-side search. */
  searchText: (row: T) => string;
  /** Fields for the @ui FilterBuilder (Filters popover). */
  filterFields?: FilterField[];
  /** Value of a filterable field for a row — used to apply the built conditions. */
  filterAccessor?: (row: T, field: string) => string;
  emptyState?: ReactNode;
}

/** Apply one FilterBuilder condition to a field value. */
function matchCondition(val: string, operator: string, target: string): boolean {
  const v = (val ?? '').toLowerCase();
  const t = (target ?? '').toLowerCase();
  const list = t.split(',').map((s) => s.trim()).filter(Boolean);
  switch (operator) {
    case 'not_contains': return !v.includes(t);
    case 'equals': case 'is': return v === t;
    case 'not_equals': case 'is_not': return v !== t;
    case 'starts_with': return v.startsWith(t);
    case 'ends_with': return v.endsWith(t);
    case 'is_any_of': return list.includes(v);
    case 'is_none_of': return !list.includes(v);
    default: return v.includes(t); // contains
  }
}

/**
 * Canonical list-page scaffold (mirrors the @ui ListPage / DnsRecords template):
 * PageHeader + Toolbar (Search · Filters popover → FilterBuilder · Display
 * options · primary action) over a DataTable with row selection + column
 * visibility. Search and the built filter conditions apply client-side; each
 * condition is matched against `filterAccessor(row, field)`.
 */
export function AccountListScaffold<T>({
  title, breadcrumbLabel, description, primaryAction, banner, searchPlaceholder = 'Search…',
  rows, isLoading, isError, onRetry, getRowId, columns, toggleableIds,
  searchText, filterFields, filterAccessor, emptyState,
}: AccountListScaffoldProps<T>) {
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const toggleable = toggleableIds ?? columns.map((c) => c.id);
  const [visible, setVisible] = useState<Record<string, boolean>>(
    Object.fromEntries(toggleable.map((id) => [id, true])),
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => !toggleable.includes(c.id) || visible[c.id]),
    [columns, toggleable, visible],
  );

  const data = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !searchText(r).toLowerCase().includes(q)) return false;
      if (conditions.length && filterAccessor) {
        return conditions.every((c) => matchCondition(filterAccessor(r, c.field), c.operator, c.value));
      }
      return true;
    });
  }, [rows, query, conditions, searchText, filterAccessor]);

  return (
    <Container size="full">
      <PageHeader
        title={title}
        description={description}
        actions={primaryAction}
        breadcrumbs={breadcrumbLabel ? <AccountBreadcrumbs current={breadcrumbLabel} /> : undefined}
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {banner ?? null}

      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            {filterFields ? (
              <Popover open={filterOpen} onOpenChange={setFilterOpen}>
                <PopoverTrigger asChild>
                  <Button variant="secondary" startIcon={<Filter size={15} />}>
                    Filters {conditions.length ? <Badge tone="primary">{conditions.length}</Badge> : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" style={{ padding: 0 }}>
                  <FilterBuilder
                    fields={filterFields}
                    defaultValue={conditions.length ? conditions : undefined}
                    onApply={(c) => { setConditions(c.filter((x) => x.value !== '')); setFilterOpen(false); }}
                    onClose={() => setFilterOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            ) : null}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>Display options</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {columns.filter((c) => toggleable.includes(c.id)).map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label ?? (typeof c.header === 'string' ? c.header : c.id)}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {isError ? (
        <Alert tone="danger" title="Failed to load" actions={onRetry ? <Button size="sm" variant="ghost" onClick={onRetry}>Retry</Button> : undefined} />
      ) : isLoading ? (
        <Card flat><CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 24 }} />)}</CardBody></Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={data}
          getRowId={getRowId}
          enableSelection
          emptyState={emptyState}
          footer={`Showing ${data.length} of ${rows.length}`}
        />
      )}
      </div>
    </Container>
  );
}
